import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

import { useRef, useState, useEffect, useLayoutEffect, useCallback } from "react";
import { STRATEGY_WHITE_LIST, SSR_ONLY, ON, WHEN_VISIBLE } from "./constants";
import StrategyTypeError from "./errors/StrategyTypeError";
import StrategyOptionsError from "./errors/StategyOptionsError";
import getQAPI from "../../utils/getQAPI";
/**
 * @typedef ON
 * @type { Array<string> | string } events событие или события необходимые для запуска
 * */

/**
 * @typedef WHEN_VISIBLE
 * @type { String } rootMargin
 * @type { Element | Document } root
 * @type { Number | Array<Number> } threshold
 * */

var getGlobalBrowser = function getGlobalBrowser() {
  return typeof window === "undefined" ? null : window;
};

var useIsomorphicLayoutEffect = getGlobalBrowser() ? useLayoutEffect : useEffect;
/**
 * @template R typeof of ref object
 * @param { String } strategy Стратегия гидрации
 * @param { ON | WHEN_VISIBLE | undefined  } options Опции для стратегии
 * @return @component
 * */

export var useLazyHydrate = function useLazyHydrate(_ref, ref) {
  var strategy = _ref.strategy,
      options = _ref.options;

  if (!STRATEGY_WHITE_LIST.includes(strategy)) {
    throw new StrategyTypeError(strategy);
  }

  var qapi = getQAPI();
  var cleanupFns = useRef(new Set());

  var _useState = useState(qapi.mode === "development" || !getGlobalBrowser()),
      _useState2 = _slicedToArray(_useState, 2),
      hydrated = _useState2[0],
      setHydrated = _useState2[1];

  var runHydrate = useCallback(function () {
    return setHydrated(true);
  }, []);
  useIsomorphicLayoutEffect(function () {
    // No SSR Content
    if (!ref.current.hasChildNodes()) {
      setHydrated(true);
    }
  }, []);
  useEffect(function () {
    if (!hydrated) {
      return;
    }

    cleanupFns.current.forEach(function (fn) {
      return fn();
    });
    cleanupFns.current.clear();
  }, [hydrated]);
  useEffect(function () {
    if (hydrated) {
      return;
    }

    switch (strategy) {
      case SSR_ONLY:
        return;

      case ON:
        {
          var events = options.events;

          if (!events) {
            throw new StrategyOptionsError(strategy, options);
          }

          if (!Array.isArray(events)) {
            events = [events];
          }

          events.forEach(function (event) {
            return ref.current.addEventListener(event, runHydrate, {
              once: true,
              capture: true,
              passive: true
            });
          });
          events.forEach(function (event) {
            return cleanupFns.add(function () {
              return ref.current.removeEventListener(event, runHydrate);
            });
          });
          break;
        }

      case WHEN_VISIBLE:
        {
          var global = getGlobalBrowser();

          if (!global.IntersectionObserver) {
            console.info("IntersectionObserver is not supported by your browser");
            return runHydrate();
          }

          var ioOptions = _objectSpread({
            root: null,
            rootMargin: "150px"
          }, options);

          var io = new IntersectionObserver(function (_ref2) {
            var _ref3 = _slicedToArray(_ref2, 1),
                entry = _ref3[0];

            return entry.isIntersecting && runHydrate();
          }, ioOptions);
          io.observe(ref.current);
          cleanupFns.current.add(function () {
            return io.unobserve(ref.current);
          });
          break;
        }

      default:
        break;
    }

    return function () {
      return cleanupFns.current.forEach(function (fn) {
        return fn();
      });
    };
  }, [strategy, options]);
  return hydrated;
};